// GestureTrail — SVG-overlay som ritar live-svans bakom 1-finger-svep
// på tom canvas. Lyser upp + visar riktnings-hint när arc-svepet matchar
// FWD/BACK sheet-switch kriterierna.
//
// Renderas absolute-positioned fixed över hela viewporten med
// pointer-events: none så svansen aldrig stör touch-events.
//
// Path-data kommer från useCanvasTouchGestures via `pathRefs` (ref +
// tick). Vi använder useSyncExternalStore-mönstret för att re-render:a
// vid varje frame där pathRef ändrats utan att skapa state-uppdateringar
// inifrån listener:n (vilket skulle re-binda DOM-listeners).

import { useEffect, useState, useMemo, type RefObject } from 'react';
import { detectArc } from '@/lib/gestures/arcSwipeDetector';
import type { Point } from '@/lib/gestures/quadrant';
import { useIsMobile } from '@/hooks/use-mobile';

interface GestureTrailProps {
  pathRef: RefObject<Point[]>;
  tickRef: RefObject<number>;
}

const STROKE_BASE = 'hsl(var(--primary) / 0.35)';
const STROKE_ACTIVE = 'hsl(var(--primary) / 0.85)';

export function GestureTrail({ pathRef, tickRef }: GestureTrailProps) {
  const isMobile = useIsMobile();
  const [tickValue, setTickValue] = useState(0);

  // Polla tick-värdet — billigare än att kräva att gesten-hooken trigggar
  // setState (vilket skulle skapa cirkel-dep eller re-binding-storm).
  useEffect(() => {
    if (!isMobile) return;
    let rafId: number;
    const tick = () => {
      const current = tickRef.current ?? 0;
      setTickValue((prev) => (prev === current ? prev : current));
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isMobile, tickRef]);

  const path = pathRef.current ?? [];
  const arcResult = useMemo(() => {
    if (path.length < 4) return null;
    return detectArc(path, { width: window.innerWidth, height: window.innerHeight });
    // tickValue dep:en triggar re-evaluering varje frame
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickValue, path.length]);

  if (!isMobile || path.length < 2) return null;

  const d = path.reduce((acc, p, i) => {
    return acc + (i === 0 ? `M${p.x},${p.y}` : ` L${p.x},${p.y}`);
  }, '');

  const stroke = arcResult ? STROKE_ACTIVE : STROKE_BASE;
  const last = path[path.length - 1];

  return (
    <svg
      className="fixed inset-0 z-50 pointer-events-none"
      width="100%"
      height="100%"
      style={{ touchAction: 'none' }}
      aria-hidden="true"
    >
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={arcResult ? 4 : 3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {arcResult && (
        <g transform={`translate(${last.x + 12} ${last.y - 24})`}>
          <rect
            x={-2}
            y={-14}
            rx={6}
            width={86}
            height={20}
            fill="hsl(var(--primary))"
            opacity={0.92}
          />
          <text
            x={4}
            y={0}
            fontSize={12}
            fontWeight={600}
            fill="hsl(var(--primary-foreground))"
          >
            {arcResult === 'fwd' ? '→ Nästa sheet' : '← Föregående'}
          </text>
        </g>
      )}
    </svg>
  );
}
